import type { CSSProperties, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { HelpLabel } from '@/shared/ui/InfoTip';

import type { Dimension, ScoreFactors } from '../api/scoring';
import { calcNote, calcRows, percent, points, severityWeights } from '../model/factors';
import type { CalcNote, CalcRow } from '../model/factors';

/* How the number is calculated, with this dataset's own weights: one row per dimension, its
 * configured share, the share it actually holds in this score, and what it earned and lost.
 *
 * **Both shares are shown because they differ whenever a weighted dimension is left out**, and that
 * redistribution is the one thing about the arithmetic a reader cannot guess: "configured 30%" counting
 * for 33.3% here. The footer adds to 100 / the score / the points lost, and the note under it says
 * which dimensions moved and by how much, worked once with real numbers.
 *
 * **A dimension without points says why in words** — no evidence, cannot be measured, not in the
 * model — and its result is hatched, never an empty bar that reads as a zero.
 *
 * The run behind the score is linked rather than named: it has no name, and an execution id is not
 * something a reader can use.
 */

export interface ScoreCalculationProps {
  readonly factors: ScoreFactors;
  readonly executionId: string;
  readonly selected: Dimension | null;
  readonly onSelect: (dimension: Dimension) => void;
}

export function ScoreCalculation({
  factors,
  executionId,
  selected,
  onSelect,
}: ScoreCalculationProps): ReactElement {
  const { t, i18n } = useTranslation();
  const rows = calcRows(factors);
  const value = factors.value;
  const scored = rows.some((row) => row.state === 'scored');
  const note = calcNote(factors);
  const list = new Intl.ListFormat(i18n.language, { type: 'conjunction' });
  const title =
    value === null
      ? t('dataset.score.calc.titleNothing')
      : t('dataset.score.calc.title', { value: points(value) });

  return (
    <article className="lq-sf__card">
      <header className="lq-sf__cardHead lq-sf__cardHead--end">
        <span className="lq-sf__cardTitles">
          <HelpLabel subject={title} help={t('help.term.qualityDimension')}>
            <h2 className="lq-sf__cardTitle">{title}</h2>
          </HelpLabel>
          <span className="lq-sf__cardLead">{t('dataset.score.calc.lead')}</span>
        </span>
        <span className="lq-sf__weights">
          <span className="lq-sf__weightsLabel">
            {t('dataset.score.calc.severityWeights')}
          </span>
          {severityWeights(factors).map((entry) => (
            <span key={entry.severity} className="lq-sf__weightChip">
              {t('dataset.score.calc.severityChip', {
                severity: entry.severity,
                weight: entry.weight,
              })}
            </span>
          ))}
        </span>
      </header>

      <div className="lq-sf__calcScroll">
        <table className="lq-sf__calcTable">
          <thead>
            <tr>
              <th scope="col">{t('dataset.score.calc.col.dimension')}</th>
              <th scope="col" className="lq-sf__num">
                {t('dataset.score.calc.col.configured')}
              </th>
              <th scope="col" className="lq-sf__num">
                {t('dataset.score.calc.col.share')}
              </th>
              <th scope="col" className="lq-sf__num">
                {t('dataset.score.calc.col.available')}
              </th>
              <th scope="col" className="lq-sf__resultCol">
                {t('dataset.score.calc.col.result')}
              </th>
              <th scope="col" className="lq-sf__num">
                {t('dataset.score.calc.col.earned')}
              </th>
              <th scope="col" className="lq-sf__num">
                {t('dataset.score.calc.col.lost')}
              </th>
              <th scope="col">{t('dataset.score.calc.col.checks')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <CalcLine
                key={row.dimension}
                row={row}
                selected={selected === row.dimension}
                onSelect={onSelect}
              />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{t('dataset.score.calc.total')}</th>
              <td className="lq-sf__num">100%</td>
              <td className="lq-sf__num">{scored ? '100%' : '—'}</td>
              <td className="lq-sf__num">{scored ? points(100) : '—'}</td>
              <td />
              <td className="lq-sf__num">{value === null ? '—' : points(value)}</td>
              <td className="lq-sf__num lq-sf__lostCell">
                {factors.points_lost === null
                  ? '—'
                  : factors.points_lost > 0
                    ? `−${points(factors.points_lost)}`
                    : points(0)}
              </td>
              <td>
                {t('dataset.score.calc.totalChecks', { count: factors.rules.length })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="lq-sf__calcFoot">
        {/* A score carried by one dimension is that dimension's score, which is the one note
            worth the warning tone. */}
        <p
          className="lq-sf__calcNote"
          data-tone={note.kind === 'single' ? 'warn' : undefined}
        >
          {noteText(t, note, list)}
        </p>
        <p className="lq-sf__calcMeta">
          {t('dataset.score.modelMarker', { version: factors.score_model_version })}
          {' · '}
          <Link to={`${SCREEN_PATHS.executions}/${executionId}`}>
            {t('dataset.score.calc.run')}
          </Link>
        </p>
      </div>
    </article>
  );
}

function CalcLine({
  row,
  selected,
  onSelect,
}: {
  readonly row: CalcRow;
  readonly selected: boolean;
  readonly onSelect: (dimension: Dimension) => void;
}): ReactElement {
  const { t } = useTranslation();
  const name = t(`scoring.dimension.${row.dimension}`);
  const scored = row.state === 'scored';
  return (
    <tr data-state={row.state} data-selected={selected ? 'true' : undefined}>
      <th scope="row">
        {scored ? (
          <button
            type="button"
            className="lq-sf__calcSelect"
            aria-pressed={selected}
            title={t('dataset.score.calc.select', { dimension: name })}
            onClick={() => {
              onSelect(row.dimension);
            }}
          >
            {name}
          </button>
        ) : (
          name
        )}
      </th>
      <td className="lq-sf__num">
        {row.configured === null
          ? t('dataset.score.calc.notWeighted')
          : percent(row.configured, 0)}
      </td>
      <td className="lq-sf__num">
        {row.share !== null
          ? percent(row.share)
          : row.state === 'not_weighted'
            ? '—'
            : t('dataset.score.calc.sharedOut')}
      </td>
      <td className="lq-sf__num">
        {row.available === null ? '—' : points(row.available)}
      </td>
      <td>
        {scored && row.value !== null ? (
          <span className="lq-sf__result">
            <span
              className="lq-sf__resultTrack"
              aria-hidden="true"
              style={{ '--sf-result': `${row.value.toFixed(1)}%` } as CSSProperties}
            >
              <span className="lq-sf__resultFill" />
            </span>
            <span className="lq-sf__resultValue">{row.value.toFixed(1)}</span>
          </span>
        ) : (
          <span className="lq-sf__result" data-absent={row.state}>
            {row.state === 'not_weighted' ? null : (
              <span className="lq-sf__resultHatch" aria-hidden="true" />
            )}
            <span className="lq-sf__resultWhy">
              {t(`dataset.score.calc.state.${row.state}`)}
            </span>
          </span>
        )}
      </td>
      <td className="lq-sf__num">{row.earned === null ? '—' : points(row.earned)}</td>
      <td
        className="lq-sf__num lq-sf__lostCell"
        data-lost={row.lost !== null && row.lost > 0}
      >
        {row.lost === null ? '—' : row.lost > 0 ? `−${points(row.lost)}` : points(0)}
      </td>
      <td className="lq-sf__checksCell">
        {row.rulesCounted > 0
          ? row.rulesWithout > 0
            ? t('dataset.score.calc.rulesSilent', {
                count: row.rulesCounted,
                silent: row.rulesWithout,
              })
            : t('dataset.score.calc.rules', { count: row.rulesCounted })
          : row.rulesWithout > 0
            ? t('dataset.score.calc.onlySilent', { silent: row.rulesWithout })
            : '—'}
      </td>
    </tr>
  );
}

function noteText(t: TFunction, note: CalcNote, list: Intl.ListFormat): string {
  switch (note.kind) {
    case 'nothing':
      return t('dataset.score.calc.note.nothing');
    case 'single':
      return t('dataset.score.calc.note.single', {
        dimension: t(`scoring.dimension.${note.dimension}`),
        configured: note.configured === null ? '—' : percent(note.configured, 0),
      });
    case 'redistributed':
      return t('dataset.score.calc.note.redistributed', {
        dimensions: list.format(
          note.leftOut.map((name) => t(`scoring.dimension.${name}`)),
        ),
        count: note.leftOut.length,
        measured: note.measured,
        example: t(`scoring.dimension.${note.example.dimension}`),
        weight: note.example.weight,
        total: note.example.total,
        share: percent(note.example.share),
      });
    case 'asConfigured':
      return t('dataset.score.calc.note.asConfigured');
  }
}
